import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, isStaff, isClient, isContractor, homePathFor } from "@/context/AuthContext";

function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Gate for staff-facing (office / super-admin) routes.
export function StaffGuard() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (isClient(user)) return <Navigate to="/portal" replace />;
  if (isContractor(user)) return <Navigate to="/partner" replace />;
  if (!isStaff(user)) return <Navigate to="/login" replace />;
  return <Outlet />;
}

// Gate for client-portal routes.
export function ClientGuard() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  if (!isClient(user)) return <Navigate to={homePathFor(user)} replace />;
  return <Outlet />;
}

// Gate for the contractor marketplace portal.
export function ContractorGuard() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!isContractor(user)) return <Navigate to={homePathFor(user)} replace />;
  return <Outlet />;
}

// Public root: marketing landing page for guests; signed-in users go to
// their home (staff → dashboard, client → portal, contractor → partner).
export function RootGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={homePathFor(user)} replace />;
  return children;
}

// Sends a signed-in user to the right home; used on the login pages.
export function GuestGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (user) return <Navigate to={homePathFor(user)} replace />;
  return children;
}
